import { useTranslation } from 'react-i18next';

import {
  DataPanel,
  DataPanelAutoWrapInlineItem,
  DataPanelItem,
  DataPanelRow,
  DebugView,
  NgButton,
} from '@/ui';
import type { AppleEedCallInformation, AppleEedCallerInformation } from '@ngsos/portal-core/commands';
import { formatAppleEedHeight, formatAppleEedWeight } from '@ngsos/portal-core/incidents';
import { ClassificationPanelItem, type ClassificationItem } from '../shared/ClassificationPanelItem';
import { AppleEedEmergencyContactPanelItem } from './AppleEedEmergencyContactPanelItem';

export type AppleEedSectionProps = {
  appleEedCallInformation: AppleEedCallInformation | null;
  appleEedMedicalData: AppleEedCallerInformation | null;
  appleEedBatteryLevel: string | null;
  canRetrieveAdditionalData: boolean;
  canRequestMedia: boolean;
  askedForAppleEedMedicalData: boolean;
  appleEedMedicalDataIsLoading: boolean;
  askedForAppleEedMedia: boolean;
  getAppleEedMedicalData: () => void | Promise<void>;
  requestAppleEedMedia: () => void | Promise<void>;
};

export function AppleEedSection({
  appleEedCallInformation,
  appleEedMedicalData,
  appleEedBatteryLevel,
  canRetrieveAdditionalData,
  canRequestMedia,
  askedForAppleEedMedicalData,
  appleEedMedicalDataIsLoading,
  askedForAppleEedMedia,
  getAppleEedMedicalData,
  requestAppleEedMedia,
}: AppleEedSectionProps) {
  const { t } = useTranslation();

  if (!canRetrieveAdditionalData && !canRequestMedia) {
    return null;
  }

  const mediaRequest = (
    <>
      <NgButton
        text={t('Incident_InfoPanel_AppleEed_RequestMedia')}
        onClick={requestAppleEedMedia}
        icon="fal fa-image"
        testID="incident-request-apple-eed-media-button"
        enableMultipleClickPrevention
        showProgress
      />
      {askedForAppleEedMedia && <div>{t('Incident_InfoPanel_AppleEed_MediaRequested')}</div>}
    </>
  );

  const classificationItems: ClassificationItem[] = [];
  if (appleEedCallInformation?.trigger_type === 'Fall Detection') {
    classificationItems.push({
      title: t('Incident_InfoPanel_AppleEed_FallDetection'),
      image: 'Images/Incident/AppleEed/fall_detection.svg',
      testID: 'incident-apple-eed-fall-detection-badge',
    });
  }
  if (appleEedCallInformation?.trigger_type === 'Crash Detection') {
    classificationItems.push({
      title: t('Incident_InfoPanel_AppleEed_CrashDetection'),
      image: 'Images/Incident/AppleEed/crash_detection.svg',
      testID: 'incident-apple-eed-crash-detection-badge',
    });
  }

  return (
    <DataPanel header="Apple EED data">
      {appleEedMedicalDataIsLoading && <AppleEedLoadingPlaceholder />}

      {!appleEedMedicalDataIsLoading &&
        canRetrieveAdditionalData &&
        (appleEedMedicalData !== null ? (
          <>
            <ClassificationPanelItem items={classificationItems} />

            {appleEedBatteryLevel != null && (
              <DataPanelAutoWrapInlineItem
                title={t('Incident_InfoPanel_BatteryLevel')}
                value={appleEedBatteryLevel}
                testID="incident-apple-eed-battery-level-text"
              />
            )}

            <DataPanelRow>
              <DataPanelItem
                title={t('Incident_InfoPanel_Age')}
                value={appleEedMedicalData.age?.toString()}
                showEmptyPlaceholder
                testID="incident-apple-eed-age-text"
              />

              <DataPanelItem
                title={t('Incident_InfoPanel_CallerHeight')}
                value={formatAppleEedHeight(appleEedMedicalData.height)}
                contentCssClass="text-center"
                showEmptyPlaceholder
                testID="incident-apple-eed-height-text"
              />

              <DataPanelItem
                title={t('Incident_InfoPanel_CallerWeight')}
                value={formatAppleEedWeight(appleEedMedicalData.weight)}
                contentCssClass="text-end"
                showEmptyPlaceholder
                testID="incident-apple-eed-weight-text"
              />
            </DataPanelRow>

            <DebugView>
              <DataPanelAutoWrapInlineItem
                title={t('Incident_InfoPanel_AppleEed_TriggerType')}
                value={appleEedCallInformation?.trigger_type ?? undefined}
                showEmptyPlaceholder
                testID="incident-apple-eed-trigger-type-text"
              />
            </DebugView>

            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_Name')}
              value={appleEedMedicalData.name ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-name-text"
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_GestationalAge')}
              value={appleEedMedicalData.gestational_age ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-gestational-age-text"
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_Allergies')}
              value={appleEedMedicalData.allergies ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-allergies-text"
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_BloodType')}
              value={appleEedMedicalData.blood_type ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-blood-type-text"
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_Medications')}
              value={appleEedMedicalData.medications ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-medications-text"
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_MedicalConditions')}
              value={appleEedMedicalData.medical_conditions ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-medical-conditions-text"
            />
            <DataPanelAutoWrapInlineItem
              title={t('Incident_InfoPanel_AppleEed_MedicalNotes')}
              value={appleEedMedicalData.medical_notes ?? undefined}
              showEmptyPlaceholder
              testID="incident-apple-eed-medical-notes-text"
            />

            <AppleEedEmergencyContactPanelItem contacts={appleEedMedicalData.emergency_contacts} />
          </>
        ) : askedForAppleEedMedicalData ? (
          <>
            {appleEedBatteryLevel != null && (
              <DataPanelItem
                title={t('Incident_InfoPanel_BatteryLevel')}
                value={appleEedBatteryLevel}
                testID="incident-apple-eed-battery-level-text"
              />
            )}
              <div
                className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center"
                data-testid="incident-apple-eed-no-data-panel"
              >
                {t('Incident_InfoPanel_AppleEed_NoAppleEedAdditionalData')}
                {canRequestMedia && mediaRequest}
            </div>
          </>
        ) : (
          <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
            {t('Incident_InfoPanel_AppleEed_RetrieveInfo_Line1')}
            <NgButton
              text={t('Incident_InfoPanel_AppleEed_RetrieveInfo')}
              onClick={getAppleEedMedicalData}
              testID="incident-request-apple-eed-data-button"
              enableMultipleClickPrevention
              showProgress
              />
            {canRequestMedia && mediaRequest}
          </div>
        ))}

      {!appleEedMedicalDataIsLoading && canRequestMedia && appleEedMedicalData !== null && (
        <div className="d-flex flex-column align-items-center gap-2 p-2 medium text-center">{mediaRequest}</div>
      )}

      {!appleEedMedicalDataIsLoading && !canRetrieveAdditionalData && canRequestMedia && (
        <div className="d-flex flex-column align-items-center gap-2 p-2 bg-chat-dispatcher medium text-center">
          {mediaRequest}
        </div>
      )}
    </DataPanel>
  );
}

function AppleEedLoadingPlaceholder() {
  return (
    <div className="d-flex flex-column gap-1 p-2 placeholder-glow">
      <div className="placeholder col-8"></div>
      <div className="placeholder col-4"></div>
      <div className="placeholder col-7"></div>
      <div className="placeholder col-5"></div>
      <div className="placeholder col-9"></div>
    </div>
  );
}
